import { errorEnvelopeSchema } from "@egalregal/shared";
import { ApiError } from "../../shared/api/errors";
import type { Translate } from "../../shared/i18n";
import type { PlacementOutcome } from "./placements";

/** A placement write the ledger could not save. */
export type PlacementFailure = Exclude<PlacementOutcome, { status: "saved" }>;

/**
 * The one failure wording every placement surface shows (placement-ledger 03).
 *
 * The ledger's outcome already splits a write that never reached the server
 * (`unreachable`) from one the server refused (`refused`); this maps that split
 * onto the distinct notices from placement-ledger 01, so no surface re-derives
 * the `UnreachableError` check. A refusal carries the server's own envelope
 * message when there is one, so the user sees why the write was rejected; the
 * generic notice is the fallback. A `saved` outcome has no failure wording.
 *
 * @param t the typed translator bound to the current language.
 * @param failure the ledger's non-saved outcome.
 * @returns the notice to show inline or in the surface's snackbar.
 */
export function placementFailureNotice(t: Translate, failure: PlacementFailure): string {
  if (failure.status === "unreachable") return t("offlineNotSaved");
  const reason = refusalReason(failure.error);
  return reason === undefined
    ? t("scanAdjustFailed")
    : t("scanAdjustFailedReason", { reason });
}

/**
 * The server's own wording from an error envelope, when the refusal carries
 * one. Anything else (a plain `Error`, a non-envelope body, an empty message)
 * has no reason to show.
 */
function refusalReason(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const parsed = errorEnvelopeSchema.safeParse(error.body);
  if (!parsed.success) return undefined;
  const message = parsed.data.error.message.trim();
  return message === "" ? undefined : message;
}
